import React, { useCallback, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import {
  useHumanReviewProjectMutationsTransport,
  useHumanReviewProjectsTransport,
  useHumanReviewProjectWithSimulationIdsFetcherTransport,
  useProjectAssignmentEmailTransport,
} from "@/human-feedback/transport"
import { Button, cn, TABLE_CARD_CLASS } from "@coval/ui"
import { Trash2 } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { DeleteRunsModal } from "@/components/modals/DeleteRunsModal"
import { AddToHumanReviewProjectDropdown } from "@/components/table-actions/AddToHumanReviewProjectDropdown"
import { AddToTestSetButton } from "@/components/table-actions/AddToTestSetButton"
import { ReportButton } from "@/components/table-actions/ReportButton"
import { TableCsvDownloadButton } from "@/components/table-actions/TableCsvDownloadButton"
import {
  TableDisplayScope,
  TableDisplaySlot,
} from "@/components/tables/TableDisplayScope"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { usePageCommands } from "@/hooks/usePageCommands"
import { usePageShortcuts } from "@/hooks/usePageShortcuts"
import { useQueryParams } from "@/hooks/useQueryParams"
import {
  convertConversationsDataToCSV,
  downloadCSV,
} from "@/utils/csv/csv-export"
import { toISODateString } from "@/utils/date/date-range-helpers"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import { useConversationsNavigation } from "../contexts/ConversationsNavigationContext"
import { ConversationsPopoverProvider } from "../contexts/ConversationsPopoverContext"
import { useConversationsBulkDelete } from "../hooks/useConversationsBulkDelete"
import { useConversationsInfiniteQuery } from "../hooks/useConversationsInfiniteQuery"
import { useConversationsMetricTableState } from "../hooks/useConversationsMetricTableState"
import { useConversationsNavigationDataSync } from "../hooks/useConversationsNavigationDataSync"
import { useConversationsSearchState } from "../hooks/useConversationsSearchState"
import { useConversationsSessionState } from "../hooks/useConversationsSessionState"
import { useConversationsTableNavigation } from "../hooks/useConversationsTableNavigation"
import { useConversationsTableSelection } from "../hooks/useConversationsTableSelection"
import { useConversationsTableSessionRestore } from "../hooks/useConversationsTableSessionRestore"
import { useConversationsVisualizerFilterCoordination } from "../hooks/useConversationsVisualizerFilterCoordination"
import { useUploadedConversationsVisualizerFeatureFlag } from "../hooks/useUploadedConversationsVisualizerFeatureFlag"
import { useAddToTestSetTransport } from "../routes/useAddToTestSetTransport"
import {
  buildConversationsFiltersSnapshot,
  buildConversationsReportSelection,
  getConversationsTableView,
} from "../utils/conversationsTableState"
import { BulkTagDropdown } from "./BulkTagDropdown"
import { ConversationSearchInput } from "./ConversationSearchInput"
import { ConversationsErrorState } from "./ConversationsErrorState"
import { ConversationsFilterChips } from "./ConversationsFilterChips"
import { ConversationsSummaryPublisher } from "./ConversationsSummaryPublisher"
import { ConversationsFilterDropdown } from "./filters"
import { ProjectFilterTruncationBanner } from "./ProjectFilterTruncationBanner"
import { RerunMetricsButton } from "./RerunMetricsButton"
import { SavedViewsDropdown } from "./SavedViewsDropdown"
import { ColumnSelector } from "./uploaded-conversations-table/index"
import { UploadedConversationsEmptyState } from "./UploadedConversationsEmptyState"
import { UploadedConversationsVisualizerPanel } from "./UploadedConversationsVisualizerPanel"
import { VirtualConversationsTable } from "./VirtualConversationsTable"

interface ConversationsTableProps {
  onRowClick: (simulationOutputId: string) => void
  onReport: (selection: {
    runIds: string[]
    simulationOutputIds: string[]
  }) => void
  onRequestUpload: () => void
  organizationId: string
  searchActions?: React.ReactNode
}

/** Keyset-paginated conversations with virtual scrolling and session restoration. */
export const ConversationsTable: React.FC<ConversationsTableProps> = ({
  onRowClick,
  onReport,
  onRequestUpload,
  organizationId,
  searchActions,
}) => {
  const { trackEvent } = useAnalytics()
  const queryParams = useQueryParams()
  const router = useRouter()
  const { slug } = router.query
  const searchInputRef = useRef<HTMLInputElement>(null)
  const {
    clusterFilterIds,
    handleClearSearch,
    handleSearchQueryChange,
    searchQuery,
    searchResetKey,
    searchType,
    setClusterFilterIds,
    setSearchQuery,
    setSearchType,
  } = useConversationsSearchState()
  const {
    data: humanReviewProjects = [],
    isPending: isProjectsLoading,
    isError: isProjectsError,
    refetch: refetchProjects,
  } = useHumanReviewProjectsTransport()
  const fetchHumanReviewProjectWithSimulationIds =
    useHumanReviewProjectWithSimulationIdsFetcherTransport()
  const { updateProject: updateHumanReviewProject } =
    useHumanReviewProjectMutationsTransport()
  const { mutateAsync: sendProjectUpdate } =
    useProjectAssignmentEmailTransport()
  const {
    testSets,
    fetchTranscript,
    addToExistingTestSet,
    createTestSetWithRows,
  } = useAddToTestSetTransport()
  const {
    clearSelectedKeys,
    handleClearSelection,
    handleSelectionChange,
    restoreSelection,
    runsToDelete,
    selectedCount,
    selectedKeys,
    setRunsToDelete,
    setSelectedKeys,
  } = useConversationsTableSelection()
  const { saveBeforeNavigation, getSavedState, clearSavedState } =
    useConversationsSessionState()
  const uploadedConversationRestoreSessionState = useMemo(
    () => ({ clearSavedState, getSavedState }),
    [clearSavedState, getSavedState],
  )
  const uploadedConversationNavigationSessionState = useMemo(
    () => ({ clearSavedState, saveBeforeNavigation }),
    [clearSavedState, saveBeforeNavigation],
  )
  const buildUploadedConversationHref = useCallback(
    (path: string) => queryParams.getHref(path, slug as string),
    [queryParams, slug],
  )

  useEscapeShortcut({
    allowInInputs: true,
    description: "Clear selection",
    enabled: selectedCount > 0,
    handler: handleClearSelection,
    priority: ESCAPE_SHORTCUT_PRIORITY.SELECTION_CLEAR,
  })
  const {
    state: {
      dateRange,
      metricIds,
      agentIds,
      tagFilters,
      projectIds,
      metadataFilters,
      metricValueFilters,
    },
    clearAllFilters,
    hasActiveFilters,
    isReady: isFilterReady,
    registerColumnCallbacks,
  } = useConversationsFilter()

  const uploadedConversationFiltersSnapshot = useMemo(
    () =>
      buildConversationsFiltersSnapshot({
        agentIds,
        dateRange,
        metadataFilters,
        metricIds,
        metricValueFilters,
        tagFilters,
        projectIds,
      }),
    [
      agentIds,
      dateRange,
      metadataFilters,
      metricIds,
      metricValueFilters,
      tagFilters,
      projectIds,
    ],
  )

  const { initialScrollOffset, restoredRunIdsBySimulationId } =
    useConversationsTableSessionRestore({
      filters: uploadedConversationFiltersSnapshot,
      isFilterReady,
      onRestoreSelection: restoreSelection,
      sessionState: uploadedConversationRestoreSessionState,
    })

  const queryEnabled = Boolean(organizationId) && isFilterReady
  const uploadedConversationsVisualizerEnabled =
    useUploadedConversationsVisualizerFeatureFlag() === true

  const {
    data: allConversationsData,
    fetchNextPage,
    hasNextPage,
    hasSettledInitialFetch,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    error,
    refetch,
    loadedCursors,
    projectFilterTruncatedFromCount,
  } = useConversationsInfiniteQuery({
    enabled: queryEnabled,
    searchQuery,
    searchType,
    simulationOutputIds: clusterFilterIds ?? undefined,
  })

  const tableView = getConversationsTableView({
    hasSettledInitialFetch,
    isError,
    isLoading,
    rowCount: allConversationsData.length,
  })

  const isRefetching = isFetching && !isLoading && !isFetchingNextPage
  const hasActiveFiltersOrSearch =
    hasActiveFilters || Boolean(searchQuery.trim() || clusterFilterIds?.length)

  useConversationsNavigationDataSync(allConversationsData)
  const selectedRunIdsBySimulationId = useMemo(() => {
    const runIdsBySimulationId: Record<string, string> = {}
    for (const simulationOutputId of selectedKeys) {
      const loadedRow = allConversationsData.find(
        (row) => row.simulation_output_id === simulationOutputId,
      )
      const runId =
        loadedRow?.run_id ?? restoredRunIdsBySimulationId[simulationOutputId]
      if (runId) {
        runIdsBySimulationId[simulationOutputId] = runId
      }
    }
    return runIdsBySimulationId
  }, [allConversationsData, restoredRunIdsBySimulationId, selectedKeys])

  const {
    columnsWithMetrics,
    handleVisibleColumnsReset,
    isMetricsLoading,
    isWorkspaceSettingsError,
    metricDisplayNames,
    metricLabelsById,
    metricRecords,
    metricRenderersById,
    tableRows,
    visibleColumns,
  } = useConversationsMetricTableState({
    allConversationsData,
    clusterDescriptionsEnabled: uploadedConversationsVisualizerEnabled,
    metricIds,
    registerColumnCallbacks,
    tableHasInitiallyLoaded: hasSettledInitialFetch,
  })
  const handleTrackOpen = useCallback(
    (simulationOutputId: string, openedInNewTab: boolean) => {
      trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.TABLE_ROW_CLICKED, {
        simulation_output_id: simulationOutputId,
        opened_in_new_tab: openedInNewTab,
      })
    },
    [trackEvent],
  )

  const { setNavigationData } = useConversationsNavigation()
  const {
    conversationHref,
    handleRowClick,
    handleRowOpenInNewTab,
    handleScrollPositionChange,
    openConversationResult,
  } = useConversationsTableNavigation({
    allConversationsData,
    buildHref: buildUploadedConversationHref,
    filters: uploadedConversationFiltersSnapshot,
    isSearchActive: Boolean(searchQuery.trim() || clusterFilterIds?.length),
    loadedCursors,
    onOpenResult: onRowClick,
    onTrackOpen: handleTrackOpen,
    selectedKeys,
    selectedRunIdsBySimulationId,
    sessionState: uploadedConversationNavigationSessionState,
    setNavigationData,
  })

  const {
    closeDeleteModal,
    confirmBulkDelete,
    isDeleteModalOpen,
    isDeleting,
    openBulkDelete,
  } = useConversationsBulkDelete({
    allConversationsData,
    runsToDelete,
    selectedKeys,
    setRunsToDelete,
    setSelectedKeys,
  })
  const {
    handlePointerEnter,
    handlePointerLeave,
    keyboardActiveItemId: activeRowId,
    setActiveItemId,
  } = useCollectionPageShortcuts({
    enabled: allConversationsData.length > 0,
    getItemId: (item) => item.simulation_output_id ?? item.run_id,
    itemLabel: "uploaded conversation",
    items: allConversationsData,
    onOpenItem: (item) => {
      if (item.simulation_output_id) {
        openConversationResult(item.simulation_output_id)
      }
    },
  })

  const {
    handleVisualizerClusterFilter,
    handleVisualizerConversationClick,
    tableScrollContainerRef,
  } = useConversationsVisualizerFilterCoordination({
    agentIds,
    clearAllFilters,
    clusterFilterIds,
    conversationCount: allConversationsData.length,
    dateRange,
    hasActiveFilters,
    isFetching,
    metadataFilters,
    metricIds,
    metricValueFilters,
    searchQuery,
    searchType,
    selectedKeys,
    setActiveItemId,
    setClusterFilterIds,
    setSearchQuery,
    setSearchType,
    setSelectedKeys,
    tableHasInitiallyLoaded: hasSettledInitialFetch,
    tagFilters,
    projectIds,
  })

  const [csvOpen, setCsvOpen] = useState(false)
  const handleExportCsv = useCallback(() => setCsvOpen(true), [])
  const handleDownloadCsv = useCallback(() => {
    const csvData = convertConversationsDataToCSV(
      tableRows,
      visibleColumns,
      metricLabelsById,
      undefined,
      columnsWithMetrics,
    )
    const timestamp = toISODateString()
    const filename = `uploaded-conversations-export-${timestamp}.csv`
    downloadCSV(csvData, filename, trackEvent)
  }, [
    columnsWithMetrics,
    metricLabelsById,
    tableRows,
    trackEvent,
    visibleColumns,
  ])

  const reportSelection = useMemo(() => {
    return buildConversationsReportSelection(
      selectedKeys,
      selectedRunIdsBySimulationId,
    )
  }, [selectedRunIdsBySimulationId, selectedKeys])

  const handleReport = useCallback(() => {
    if (reportSelection) {
      onReport(reportSelection)
    }
  }, [onReport, reportSelection])

  const pageShortcuts = useMemo(
    () => [
      {
        action: handleExportCsv,
        enabled: allConversationsData.length > 0 && !isLoading,
        key: "e",
        label: "Download CSV",
        state: {
          description: "Export the current uploaded conversations as CSV.",
        },
      },
    ],
    [allConversationsData.length, handleExportCsv, isLoading],
  )

  usePageShortcuts(pageShortcuts)

  const selectionCommands = useMemo(
    () => [
      {
        action: handleReport,
        available: reportSelection !== null && !isLoading,
        description:
          "Generate a report from the selected uploaded conversations.",
        id: "uploaded-conversations-selection-report",
        kind: "create" as const,
        label: "Generate report from selection",
        scope: "selection" as const,
      },
      {
        action: handleClearSelection,
        available: selectedCount > 0,
        description: "Clear the current uploaded conversation selection.",
        id: "uploaded-conversations-selection-clear",
        kind: "back" as const,
        label: "Clear selected results",
        scope: "selection" as const,
      },
    ],
    [
      handleClearSelection,
      handleReport,
      isLoading,
      reportSelection,
      selectedCount,
    ],
  )

  usePageCommands(selectionCommands)

  return (
    <TableDisplayScope
      search={searchQuery}
      filters={{ ...uploadedConversationFiltersSnapshot }}
      className={cn("w-full h-full min-w-0 flex flex-col", TABLE_CARD_CLASS)}
    >
      {isWorkspaceSettingsError && (
        <div className="table-pad border-b border-coval-danger-border bg-coval-danger-surface text-sm text-coval-danger-on-surface">
          Workspace metric settings could not be loaded. Default metric columns
          may be incomplete until the page refreshes successfully.
        </div>
      )}
      <ProjectFilterTruncationBanner
        truncatedFromCount={projectFilterTruncatedFromCount}
        hasClusterFilter={Boolean(clusterFilterIds?.length)}
      />

      <ConversationsSummaryPublisher
        enabled={queryEnabled}
        metricDisplayNames={metricDisplayNames}
        metricRecordsById={metricRecords}
      />

      {uploadedConversationsVisualizerEnabled && (
        <UploadedConversationsVisualizerPanel
          onClusterFilterRequest={handleVisualizerClusterFilter}
          onConversationClick={handleVisualizerConversationClick}
        />
      )}

      <div
        className="table-pad flex-none border-b border-coval-border"
        data-testid="uploaded-conversations-filter-bar"
      >
        <ConversationsPopoverProvider>
          <div className="flex items-center gap-2">
            <ConversationSearchInput
              inputRef={searchInputRef}
              value={searchQuery}
              resetKey={searchResetKey}
              onChange={handleSearchQueryChange}
              onClear={handleClearSearch}
              hidden={selectedCount > 0}
            />
            {selectedCount > 0 ? (
              <>
                <ReportButton
                  onClick={handleReport}
                  disabled={isLoading || reportSelection === null}
                  aria-label={`Generate report from ${selectedCount} selected uploaded conversations`}
                />
                <AddToHumanReviewProjectDropdown
                  itemType="uploaded conversation"
                  selectedKeys={selectedKeys}
                  currentData={allConversationsData}
                  humanReviewProjects={humanReviewProjects}
                  isProjectsLoading={isProjectsLoading}
                  isProjectsError={isProjectsError}
                  onRetryProjects={() => {
                    void refetchProjects()
                  }}
                  fetchHumanReviewProjectWithSimulationIds={
                    fetchHumanReviewProjectWithSimulationIds
                  }
                  updateHumanReviewProject={updateHumanReviewProject}
                  sendProjectUpdate={sendProjectUpdate}
                  onSuccess={clearSelectedKeys}
                />
                <BulkTagDropdown
                  selectedKeys={selectedKeys}
                  currentData={allConversationsData}
                  onSuccess={clearSelectedKeys}
                />
                <AddToTestSetButton
                  selectedSimulationOutputIds={Array.from(selectedKeys)}
                  testSets={testSets}
                  fetchTranscript={fetchTranscript}
                  addToExistingTestSet={addToExistingTestSet}
                  createTestSetWithRows={createTestSetWithRows}
                  onSuccess={clearSelectedKeys}
                />

                <TableDisplaySlot />
                <RerunMetricsButton
                  selectedKeys={selectedKeys}
                  currentData={allConversationsData}
                  onSuccess={clearSelectedKeys}
                />
              </>
            ) : (
              <>
                <ConversationsFilterDropdown
                  conversations={allConversationsData}
                  filterEnabled={hasSettledInitialFetch}
                />

                <TableDisplaySlot />

                <SavedViewsDropdown visibleColumns={visibleColumns} />
                {searchActions}
              </>
            )}

            {selectedCount > 0 && (
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="danger"
                  onClick={openBulkDelete}
                  aria-label={`Delete ${selectedCount} selected items`}
                >
                  <Trash2 className="size-4" />
                  Delete
                </Button>
                <span className="text-sm text-coval-text-muted">
                  {selectedCount} selected
                </span>
              </div>
            )}
          </div>
          {selectedCount === 0 && (
            <div className="mt-2 empty:hidden">
              <ConversationsFilterChips
                clusterFilterCount={clusterFilterIds?.length ?? null}
                conversations={allConversationsData}
                filterEnabled={hasSettledInitialFetch}
                onClearClusterFilter={() => setClusterFilterIds(null)}
              />
            </div>
          )}
        </ConversationsPopoverProvider>
      </div>
      <div
        className="min-h-0 min-w-0 flex-1"
        data-covi-view-list="conversation"
        data-covi-list-showing={`${allConversationsData.length} loaded${hasNextPage ? " (more available)" : ""}`}
        data-covi-list-filters={hasActiveFiltersOrSearch ? "active" : undefined}
      >
        {tableView === "error" ? (
          <div className="h-full overflow-auto">
            <ConversationsErrorState
              error={error}
              onRetry={() => {
                void refetch()
              }}
            />
          </div>
        ) : tableView === "empty" ? (
          <div className="h-full overflow-auto">
            <UploadedConversationsEmptyState
              hasActiveFilters={
                hasActiveFilters || Boolean(clusterFilterIds?.length)
              }
              searchValue={searchQuery}
              onResetFilters={() => {
                clearAllFilters()
                handleClearSearch()
                setClusterFilterIds(null)
              }}
              onRequestUpload={onRequestUpload}
            />
          </div>
        ) : (
          <VirtualConversationsTable
            columnControls={
              <ColumnSelector
                columns={columnsWithMetrics}
                visibleColumns={visibleColumns}
                onVisibleColumnsChange={handleVisibleColumnsReset}
              />
            }
            rows={tableRows}
            columns={columnsWithMetrics}
            visibleColumns={visibleColumns}
            csvControl={
              <TableCsvDownloadButton
                open={csvOpen}
                onOpenChange={setCsvOpen}
                onClick={handleDownloadCsv}
                rowCount={allConversationsData.length}
                columns={columnsWithMetrics
                  .filter(
                    (column) =>
                      visibleColumns.has(column.key) && column.key !== "audio",
                  )
                  .map((column) => column.fullLabel || column.label)}
                disabled={isLoading || allConversationsData.length === 0}
              />
            }
            metricRenderersById={metricRenderersById}
            isMetricsLoading={isMetricsLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={tableView === "loading"}
            isRefetching={isRefetching}
            activeRowId={activeRowId}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            onRowClick={handleRowClick}
            onRowOpenInNewTab={handleRowOpenInNewTab}
            conversationHref={conversationHref}
            scrollRef={tableScrollContainerRef}
            onFetchNextPage={() => {
              void fetchNextPage()
            }}
            onScrollPositionChange={handleScrollPositionChange}
            initialScrollOffset={initialScrollOffset}
            onRowPointerEnter={handlePointerEnter}
            onRowPointerLeave={handlePointerLeave}
          />
        )}
      </div>

      <DeleteRunsModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={() => {
          void confirmBulkDelete()
        }}
        itemCount={runsToDelete.length}
        isDeleting={isDeleting}
      />
    </TableDisplayScope>
  )
}
